const TRANSLATE = /translate\(\s*([-\d.]+)(?:[,\s]+([-\d.]+))?\s*\)/i;
const SVG_NUMBER = /[-+]?(?:\d*\.\d+|\d+)(?:e[-+]?\d+)?/gi;

export interface AuthoredPoint {
  x: number;
  y: number;
}

export interface AuthoredRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function accumulatedTranslate(element: Element, root: Element): AuthoredPoint {
  let x = 0;
  let y = 0;
  let node: Element | null = element;
  while (node && node !== root) {
    const transform = node.getAttribute('transform');
    const match = transform ? TRANSLATE.exec(transform) : null;
    if (match) {
      x += Number(match[1]);
      y += Number(match[2] ?? 0);
    }
    node = node.parentElement;
  }
  return { x, y };
}

function numbersFrom(value: string): number[] {
  return (value.match(SVG_NUMBER) ?? []).map(Number).filter((value) => Number.isFinite(value));
}

function boundsFromPairs(values: number[]): AuthoredRect | null {
  if (values.length < 2) return null;
  let minX = values[0];
  let minY = values[1];
  let maxX = values[0];
  let maxY = values[1];
  for (let index = 0; index + 1 < values.length; index += 2) {
    const x = values[index];
    const y = values[index + 1];
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

function localBounds(element: Element): AuthoredRect | null {
  const tag = element.tagName.toLowerCase();
  if (tag === 'rect') {
    const width = Number(element.getAttribute('width'));
    const height = Number(element.getAttribute('height'));
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return null;
    }
    return {
      x: Number(element.getAttribute('x') ?? 0),
      y: Number(element.getAttribute('y') ?? 0),
      width,
      height,
    };
  }
  if (tag === 'circle') {
    const cx = Number(element.getAttribute('cx') ?? 0);
    const cy = Number(element.getAttribute('cy') ?? 0);
    const radius = Number(element.getAttribute('r') ?? 0);
    if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
    const size = Number.isFinite(radius) && radius > 0 ? radius * 2 : 0;
    return {
      x: cx - (size / 2),
      y: cy - (size / 2),
      width: size,
      height: size,
    };
  }
  if (tag === 'ellipse') {
    const cx = Number(element.getAttribute('cx') ?? 0);
    const cy = Number(element.getAttribute('cy') ?? 0);
    const rx = Number(element.getAttribute('rx') ?? 0);
    const ry = Number(element.getAttribute('ry') ?? 0);
    if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
    return {
      x: cx - (Number.isFinite(rx) ? rx : 0),
      y: cy - (Number.isFinite(ry) ? ry : 0),
      width: Number.isFinite(rx) ? rx * 2 : 0,
      height: Number.isFinite(ry) ? ry * 2 : 0,
    };
  }
  if (tag === 'line') {
    const x1 = Number(element.getAttribute('x1') ?? 0);
    const y1 = Number(element.getAttribute('y1') ?? 0);
    const x2 = Number(element.getAttribute('x2') ?? 0);
    const y2 = Number(element.getAttribute('y2') ?? 0);
    return boundsFromPairs([x1, y1, x2, y2]);
  }
  if (typeof (element as SVGGraphicsElement).getBBox === 'function') {
    try {
      const box = (element as SVGGraphicsElement).getBBox();
      if (
        Number.isFinite(box.x) &&
        Number.isFinite(box.y) &&
        Number.isFinite(box.width) &&
        Number.isFinite(box.height) &&
        (box.width > 0 || box.height > 0)
      ) {
        return { x: box.x, y: box.y, width: box.width, height: box.height };
      }
    } catch {
      // jsdom often lacks layout; fall through to path/points parsing.
    }
  }
  if (tag === 'path') {
    return boundsFromPairs(numbersFrom(element.getAttribute('d') ?? ''));
  }
  if (tag === 'polygon' || tag === 'polyline') {
    return boundsFromPairs(numbersFrom(element.getAttribute('points') ?? ''));
  }
  return null;
}

export function elementBounds(element: Element, root: Element): AuthoredRect | null {
  const local = localBounds(element);
  if (!local) return null;
  const origin = accumulatedTranslate(element, root);
  return {
    x: origin.x + local.x,
    y: origin.y + local.y,
    width: local.width,
    height: local.height,
  };
}

export function elementCentre(element: Element, root: Element): AuthoredPoint | null {
  const tag = element.tagName.toLowerCase();
  const origin = accumulatedTranslate(element, root);
  if (tag === 'circle' || tag === 'ellipse') {
    const cx = Number(element.getAttribute('cx') ?? 0);
    const cy = Number(element.getAttribute('cy') ?? 0);
    if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
    return { x: origin.x + cx, y: origin.y + cy };
  }
  const bounds = elementBounds(element, root);
  if (!bounds) return null;
  return {
    x: bounds.x + bounds.width / 2,
    y: bounds.y + bounds.height / 2,
  };
}
